import {
  Alpha3CountryCode,
  ButtonGroup,
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  Label,
  openDialog,
  Separator,
} from 'platform/components';
import {Box, Grid, GridItem, HStack, Space, VStack} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';

import {forwardRef, useImperativeHandle, useRef, useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isFalse, isNilOrEmpty} from 'ramda-adjunct';

import {useAccM2StrediskoGetCentresQuery} from '@dms/api/finance';
import {
  PatchServiceOrderApiArg,
  PatchServiceOrderInternalApiArg,
  useGetCostCenterQuery,
  useGetServiceOrderGroupsForServiceOrderQuery,
  useGetServiceOrderInternalQuery,
  useGetServiceOrderQuery,
  useGetServiceOrderVariantAccountingQuery,
  useGetServiceOrderVariantReportingQuery,
  usePatchServiceOrderInternalMutation,
  usePatchServiceOrderMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, PredefinedNotes, usePhoneNumbers, useTenant} from '@dms/shared';
import {FuelLevelInput} from '@dms/shared-aftersales';

import {parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {MileageValidationDialog} from '../../../components/MileageValidationDialog';
import {relativeDates} from '../../../constants/relativeDates';
import {useOrderMandatoryFields} from '../hooks/useOrderMandatoryFields';
import {OrderDetailFormRef} from '../types/orderDetailFormRef';
import {orderInternalFormSchema, OrderInternalFormType} from '../types/orderInternalFormSchema';
import {getCompletionAt} from '../utils/getCompletionAt';
import {getDefaultCompletionAtTime} from '../utils/getDefaultCompletionAtTime';
import {hasObjectNonEmptyValue} from '../utils/hasObjectNonEmptyValue';
import {DriverInformationSection} from './DriverInformationSection';

interface OrderInternalFormProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  isReadOnly?: boolean;
}

export const OrderInternalForm = forwardRef<OrderDetailFormRef, OrderInternalFormProps>(
  (props, ref) => {
    const [phoneInputKey, setPhoneInputKey] = useState(0);
    const refreshPhoneInputKey = () => setPhoneInputKey((prev) => prev + 1);
    const {
      data: serviceOrderInternal,
      isFetching: isServiceOrderInternalFetching,
      isError: isServiceOrderInternalError,
    } = useGetServiceOrderInternalQuery(props);
    const {
      data: serviceOrder,
      isFetching: isServiceOrderFetching,
      isError: isServiceOrderError,
    } = useGetServiceOrderQuery(props);
    const {
      data: costCenterOptions,
      isLoading: isCostCenterLoading,
      isError: isCostCenterError,
    } = useGetCostCenterQuery();
    const {
      isFieldRequired,
      isLoading: isServiceOrderVariantMandatoryFieldsLoading,
      isError: isServiceOrderVariantMandatoryFieldsError,
    } = useOrderMandatoryFields(props.serviceOrderId);
    const {tenantCurrency} = useTenant();
    const formatCurrencySymbol = useCurrencySymbolFormatter();

    const {countriesOptions} = usePhoneNumbers();

    const {
      data: orderVariantAccounting,
      isFetching: isOrderVariantAccountingFetching,
      isError: isOrderVariantAccountingError,
    } = useGetServiceOrderVariantAccountingQuery(
      {serviceOrderVariantId: serviceOrder?.serviceOrderVariantId ?? ''},
      {skip: isNilOrEmpty(serviceOrder)}
    );

    const {
      data: orderVariantReporting,
      isFetching: isOrderVariantReportingFetching,
      isError: isOrderVariantReportingError,
    } = useGetServiceOrderVariantReportingQuery(
      {serviceOrderVariantId: serviceOrder?.serviceOrderVariantId ?? ''},
      {skip: isNilOrEmpty(serviceOrder)}
    );

    const {
      data: serviceCentres,
      isLoading: isServiceCentresLoading,
      isError: isServiceCentresError,
    } = useAccM2StrediskoGetCentresQuery();

    const {
      data: orderGroups,
      isLoading: isOrderGroupsLoading,
      isError: isOrderGroupsError,
    } = useGetServiceOrderGroupsForServiceOrderQuery({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    });

    const [patchServiceOrder] = usePatchServiceOrderMutation();
    const [patchServiceOrderInternal] = usePatchServiceOrderInternalMutation();
    const formApiRef = useRef<UseFormReturn<OrderInternalFormType>>(null);

    const onSubmit = async (data: OrderInternalFormType) => {
      const serviceOrderData: PatchServiceOrderApiArg['body'] = {
        completionAt: getCompletionAt(data.completionAtDate, data.completionAtTime),
        recipientId: serviceOrder?.recipientId,
        technicanId: serviceOrder?.technicanId,
        totalPriceEstimated: data.totalPriceEstimated,
        note: data.note,
        serviceCenterId: data.serviceCenterId,
        serviceOrderGroupId: data.serviceOrderGroupId,
        contactInformation: {
          id: data.driverId,
          name: data.driverFullName,
          email: data.driverEmail,
          phoneNumber: data.driverPhone,
          identityCard: data.driverIdentityCard,
        },
        mileage: data.mileage,
        fuelTank: (data.fuelTank ?? 0) / 100,
        cancelReason: data.cancelReason,
      };

      const orderInternalData: PatchServiceOrderInternalApiArg['body'] = {
        costCenter: data.costCenter,
        returnUsedParts: data.returnUsedParts,
      };

      const patchServiceOrderMutation = hasObjectNonEmptyValue(serviceOrderData)
        ? patchServiceOrder({
            serviceCaseId: props.serviceCaseId,
            serviceOrderId: props.serviceOrderId,
            body: serviceOrderData,
          })
            .unwrap()
            .catch(handleApiError)
        : null;
      const patchServiceOrderInternalMutation = hasObjectNonEmptyValue(orderInternalData)
        ? patchServiceOrderInternal({
            serviceCaseId: props.serviceCaseId,
            serviceOrderId: props.serviceOrderId,
            body: orderInternalData,
          })
            .unwrap()
            .catch(handleApiError)
        : null;

      await Promise.all([patchServiceOrderMutation, patchServiceOrderInternalMutation]).then(
        ([orderResponse]) => {
          if (isFalse(orderResponse?.isMileageValid)) {
            openDialog(
              <MileageValidationDialog
                highestMileage={orderResponse?.highestMileage}
                data-testid={suffixTestId('mileageValidation', props)}
              />,
              {size: 'small'}
            );
          }
        }
      );
    };

    useImperativeHandle(ref, () => ({
      submit: async () => {
        if (formApiRef.current) {
          const isValid = await formApiRef.current.trigger();
          if (isValid) {
            const values = formApiRef.current.getValues();
            await onSubmit(values);
          }
        }
      },
    }));

    const filteredServiceCentresOptions =
      serviceCentres?.m2centres
        ?.filter(
          (center) =>
            !orderVariantAccounting?.excludedServiceCenterIds?.includes(center?.code ?? '')
        )
        .map((center) => ({
          label: center?.name,
          value: center?.code,
        })) ?? [];

    const filteredOrderGroupsOptions =
      orderGroups?.map((group) => ({
        label: group?.name,
        value: group?.serviceOrderGroupId,
      })) ?? [];

    const defaultValues: Partial<OrderInternalFormType> = {
      totalPriceEstimated: serviceOrder?.totalPriceEstimated?.amount,
      completionAtDate: serviceOrder?.completionAt
        ? parseDate(serviceOrder?.completionAt)
        : undefined,
      completionAtTime: getDefaultCompletionAtTime(serviceOrder?.completionAt),
      note: serviceOrder?.note,
      costCenter: serviceOrderInternal?.costCenter,
      returnUsedParts: serviceOrderInternal?.returnUsedParts,
      contactPerson: serviceOrder?.contactInformation?.id,
      email: serviceOrder?.contactInformation?.email,
      phone: serviceOrder?.contactInformation?.phoneNumber?.number,
      mileage: serviceOrder?.mileage,
      fuelTank: Math.round((serviceOrder?.fuelTank ?? 0) * 100),
      serviceOrderGroupId:
        serviceOrder?.serviceOrderGroupId || orderVariantReporting?.defaultServiceOrderGroupId,
      serviceCenterId:
        serviceOrder?.serviceCenterId || orderVariantAccounting?.defaultServiceCenterId,
      driverId: serviceOrder?.contactInformation?.id,
      driverFullName: serviceOrder?.contactInformation?.name,
      driverEmail: serviceOrder?.contactInformation?.email,
      driverPhone: {
        countryCode:
          (serviceOrder?.contactInformation?.phoneNumber?.countryCode as Alpha3CountryCode) ??
          'CZE',
        number: serviceOrder?.contactInformation?.phoneNumber?.number ?? '',
        prefix: serviceOrder?.contactInformation?.phoneNumber?.prefix ?? '',
      },
      driverIdentityCard: serviceOrder?.contactInformation?.identityCard,
      cancelReason: serviceOrder?.cancelReason,
    };

    const isLoading =
      isOrderGroupsLoading ||
      isServiceCentresLoading ||
      isOrderVariantAccountingFetching ||
      isServiceOrderFetching ||
      isServiceOrderInternalFetching ||
      isCostCenterLoading ||
      isServiceOrderVariantMandatoryFieldsLoading ||
      isOrderVariantReportingFetching;

    const isError =
      isOrderGroupsError ||
      isServiceCentresError ||
      isOrderVariantAccountingError ||
      isServiceOrderError ||
      isServiceOrderInternalError ||
      isCostCenterError ||
      isServiceOrderVariantMandatoryFieldsError ||
      isOrderVariantReportingError;

    const isCompletionRequired = isFieldRequired('completionAtDate');

    return (
      <DataStatus isLoading={isLoading} isError={isError} minHeight={110}>
        <Form<OrderInternalFormType>
          onSubmit={onSubmit}
          experimentalZodSchema={orderInternalFormSchema(isFieldRequired)}
          defaultValues={defaultValues}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi) => {
            // Store formApi in ref
            formApiRef.current = formApi;

            return (
              <>
                <FormField control={control} name="contactPerson" type="hidden" />
                <VStack spacing={4}>
                  <Card
                    variant="inlineGrey"
                    title={i18n.t('general.labels.basicInformation')}
                    hasSeparator
                  >
                    <VStack spacing={4}>
                      <Grid columns={[1, 1, 1, 8]} spacing={4}>
                        <GridItem>
                          <FormField
                            control={control}
                            name="completionAtDate"
                            type="date"
                            relativeDates={relativeDates}
                            label={i18n.t('entity.order.labels.completionDate')}
                            isDisabled={props.isReadOnly}
                            isRequired={isCompletionRequired}
                            data-testid={suffixTestId('completionAtDate', props)}
                          />
                        </GridItem>
                        <GridItem>
                          <FormField
                            control={control}
                            name="completionAtTime"
                            type="time"
                            label={i18n.t('entity.order.labels.completionTime')}
                            isDisabled={props.isReadOnly}
                            isRequired={isCompletionRequired}
                            data-testid={suffixTestId('completionAtTime', props)}
                          />
                        </GridItem>
                        <GridItem>
                          <FormField
                            control={control}
                            name="totalPriceEstimated"
                            type="number"
                            label={i18n.t('entity.order.labels.estimatedPrice')}
                            isDisabled={props.isReadOnly}
                            isRequired={isFieldRequired('totalPriceEstimated')}
                            data-testid={suffixTestId('totalPriceEstimated', props)}
                            suffix={formatCurrencySymbol(tenantCurrency)}
                          />
                        </GridItem>
                        <GridItem>
                          <FormField
                            label={i18n.t('entity.vehicle.labels.actualMileage')}
                            name="mileage"
                            type="integer"
                            control={control}
                            minStepperValue={0}
                            isDisabled={props.isReadOnly}
                            isRequired={isFieldRequired('mileage')}
                            data-testid={suffixTestId('mileage', props)}
                            suffix={i18n.t('general.metric.km')}
                          />
                        </GridItem>
                        <GridItem span={[1, 1, 1, 4]}>
                          <Box flex={1} width="100%">
                            <FuelLevelInput
                              name="fuelTank"
                              control={control}
                              isDisabled={props.isReadOnly}
                              isRequired={isFieldRequired('fuelTank')}
                              data-testid={suffixTestId('fuelTank', props)}
                            />
                          </Box>
                        </GridItem>
                        <GridItem span={[1, 1, 1, 2]}>
                          <FormField
                            control={control}
                            name="serviceCenterId"
                            type="choice"
                            options={filteredServiceCentresOptions}
                            isLoading={isLoading}
                            label={i18n.t('entity.order.labels.serviceCenter')}
                            isDisabled={
                              props.isReadOnly ||
                              orderVariantAccounting?.isDefaultServiceCenterLocked
                            }
                            isRequired={isFieldRequired('serviceCenterId')}
                            isNotClearable
                            data-testid={suffixTestId('serviceCenter', props)}
                          />
                        </GridItem>
                        <GridItem span={[1, 1, 1, 2]}>
                          <FormField
                            control={control}
                            name="serviceOrderGroupId"
                            type="choice"
                            options={filteredOrderGroupsOptions}
                            isLoading={isLoading}
                            label={i18n.t('entity.order.labels.serviceOrderGroup')}
                            isDisabled={
                              props.isReadOnly ||
                              orderVariantReporting?.isDefaultServiceOrderGroupLocked
                            }
                            isRequired={isFieldRequired('serviceOrderGroupId')}
                            isNotClearable
                            data-testid={suffixTestId('serviceOrderGroup', props)}
                          />
                        </GridItem>
                      </Grid>
                      <Separator spacing={0} />
                      <HStack spacing={4}>
                        <Box flex={1}>
                          <FormField
                            control={control}
                            name="costCenter"
                            type="choice"
                            label={i18n.t('entity.order.labels.costCenter')}
                            isDisabled={props.isReadOnly}
                            options={costCenterOptions ?? []}
                            isRequired={isFieldRequired('costCenter')}
                            data-testid={suffixTestId('costCenter', props)}
                          />
                        </Box>
                        <Box flex={1}>
                          <Space vertical={6} />
                          <FormField
                            control={control}
                            name="returnUsedParts"
                            type="checkbox"
                            label={i18n.t('entity.order.labels.returnUsedParts')}
                            isDisabled={props.isReadOnly}
                            data-testid={suffixTestId('returnUsedParts', props)}
                          />
                        </Box>
                        <Space fillAvailable />
                        <Space fillAvailable />
                      </HStack>
                      <Separator spacing={0} />
                      <Grid columns={2}>
                        <VStack spacing={1}>
                          <HStack justify="space-between" align="flex-end">
                            <Label>{i18n.t('general.labels.note')}</Label>
                            <PredefinedNotes
                              note={formApi.watch('note') ?? null}
                              onPrefill={(note) => formApi.setValue('note', note)}
                              resource="SERVICE_CASE"
                              context="service_order"
                              isLinkVariant
                              data-testid={suffixTestId('predefinedNotes', props)}
                            />
                          </HStack>
                          <FormField
                            control={control}
                            name="note"
                            type="textarea"
                            isDisabled={props.isReadOnly}
                            data-testid={suffixTestId('note', props)}
                          />
                        </VStack>
                        <VStack spacing={1}>
                          <HStack justify="space-between" align="flex-end">
                            <Label>{i18n.t('entity.order.labels.reasonForCancellation')}</Label>
                            <PredefinedNotes
                              note={formApi.watch('cancelReason') ?? null}
                              onPrefill={(note) => formApi.setValue('cancelReason', note)}
                              context="cancel_reason"
                              resource="SERVICE_ORDER"
                              isLinkVariant
                              data-testid={suffixTestId('predefinedNotesCancelReason', props)}
                            />
                          </HStack>
                          <FormField
                            control={control}
                            name="cancelReason"
                            type="textarea"
                            isDisabled={props.isReadOnly}
                            data-testid={suffixTestId('cancelReason', props)}
                          />
                        </VStack>
                      </Grid>
                    </VStack>
                  </Card>
                  <DriverInformationSection
                    control={control}
                    formApi={formApi}
                    countriesOptions={countriesOptions}
                    phoneInputKey={phoneInputKey}
                    refreshPhoneInputKey={refreshPhoneInputKey}
                    isReadOnly={props.isReadOnly}
                    data-testid={suffixTestId('driverInformation', props)}
                  />
                  <ButtonGroup align="right">
                    <FormButton
                      control={control}
                      type="submit"
                      title={i18n.t('general.actions.saveChanges')}
                      isDisabled={props.isReadOnly}
                      data-testid={suffixTestId('save', props)}
                    />
                  </ButtonGroup>
                </VStack>
              </>
            );
          }}
        </Form>
      </DataStatus>
    );
  }
);
